"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  PaginationState,
  SortingState,
  getSortedRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { Button } from "../ui/button.tsx";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.tsx";
import * as React from "react";
import "../../styleWind.scss";
import { fetchData } from "./fetchData.ts";
import { DataTableToolbar } from "./data-table-toolbar.tsx";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db, storage } from "../../firebase/firebase";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  group: string;
  isAdmin: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  group,
  isAdmin
}: DataTableProps<TData, TValue>) {
  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const fetchDataOptions = {
    pageIndex,
    pageSize,
  };

  var dataQuery = useQuery(
    ["data", fetchDataOptions],
    () =>
      fetchData({
        ...fetchDataOptions,
        documentList: dataQuery.data?.last,
        lastPage: dataQuery.data?.lastPage,
        sorting: sorting,
        lastsorting: dataQuery.data?.lastsorting,
        filter: columnFilters,
        lastfilter: dataQuery.data?.lastfilter,
        isAdmin: isAdmin,
        group: group,
      }),
    { keepPreviousData: true }
  );

  const [sorting, setSorting] = React.useState<SortingState>([]);

  React.useEffect(() => {
    if (sorting.length != 0) {
      dataQuery.refetch();
    }
  }, [sorting]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  React.useEffect(() => {
    if (columnFilters.length != 0) {
      dataQuery.refetch();
    }
  }, [columnFilters]);

  const table = useReactTable({
    data: dataQuery.data?.rows ?? data,
    columns,
    pageCount: dataQuery.data?.pageCount ?? -1,
    state: {
      pagination,
      sorting,
      columnFilters,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableFilters: true,
  });

  return (
    <div>
      <DataTableToolbar table={table} />

      <div
        style={{ backgroundColor: "#fff" }}
        className="tw-rounded-md tw-border"
      >
        <Table
          style={{
            border: "1px solid hsl(210, 14%, 83%)",
            borderRadius: "7px",
            borderCollapse: "separate",
            borderSpacing: "0px 0px",
            overflow: "hidden",
          }}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                // className="borderRadiusImportant"
                // style={{ backgroundColor: "#eee" }}
                key={headerGroup.id}
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  className="tw-cursor-pointer"
                  onClick={() => {
                    window.location.href =
                      "/home/edituser?userid=" + row.original.id;
                  }}
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      style={{
                        borderTop: "1px solid hsl(210, 14%, 83%)",
                      }}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="tw-h-24 tw-text-center"
                >
                  Nichts gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2 tw-py-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Zurück
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Weiter
        </Button>
      </div>
    </div>
  );
}
